/**
 * Display constants for the client details sheet.
 *
 * `source_ids` is a free-form JSONB map whose keys vary by ingestion path — the
 * same system arrives under several spellings ('ecw_client_id' and 'ECW',
 * 'clarity_client_id' and 'Clarity'). Everything here canonicalizes those so one
 * system renders once, under one name.
 */

/** Canonical key → full label, used in the Identifiers tab. */
export const SOURCE_ID_LABELS: Record<string, string> = {
  mrn: 'MRN',
  member_id: 'Member ID',
  ecw_client_id: 'eCW client ID',
  clarity_client_id: 'Clarity client ID',
  php_client_id: 'PHP client ID',
  php_cin: 'PHP CIN',
  cin: 'CIN',
  wpc_client_id: 'Whole Person Care',
  safety_net_client_id: 'Safety Net client ID',
  smart_care_client_id: 'SmartCare client ID',
  guarantor_acct_no: 'Guarantor acct no',
}

/** Canonical key → compact label, used for the header chips. */
export const SOURCE_ID_SHORT_LABELS: Record<string, string> = {
  mrn: 'MRN',
  member_id: 'Member ID',
  ecw_client_id: 'eCW',
  clarity_client_id: 'Clarity',
  php_client_id: 'PHP',
  php_cin: 'PHP CIN',
  cin: 'CIN',
  wpc_client_id: 'WPC',
  safety_net_client_id: 'Safety Net',
  smart_care_client_id: 'SmartCare',
  guarantor_acct_no: 'Guarantor',
}

/**
 * Alias → canonical key. Ingestion writes some systems under a short display
 * name and others under a snake_case id; both mean the same system.
 */
export const SOURCE_ID_ALIASES: Record<string, string> = {
  memberid: 'member_id',
  ecw: 'ecw_client_id',
  clarity: 'clarity_client_id',
  php: 'php_client_id',
  partnership_health_plan_client_id: 'php_client_id',
  'safety net': 'safety_net_client_id',
  safety_net: 'safety_net_client_id',
  wpc: 'wpc_client_id',
  smart_care: 'smart_care_client_id',
  smartcare: 'smart_care_client_id',
  guarantor: 'guarantor_acct_no',
}

/**
 * Which identifiers earn the two header chips, most useful first.
 *
 * Ordered by how directly an id identifies the client, not by how many clients
 * carry it: the medical record number leads, then the client ids staff search
 * source systems by, with the guarantor account number — a billing account
 * rather than a client identifier — last. Only identifiers actually on the
 * client are shown, so a rarely populated system ranking high costs nothing; a
 * client without it falls through to the next present key.
 */
export const SOURCE_ID_HEADER_PRIORITY: readonly string[] = [
  'mrn',
  'ecw_client_id',
  'cin',
  'clarity_client_id',
  'php_client_id',
  'wpc_client_id',
  'smart_care_client_id',
  'safety_net_client_id',
  'member_id',
  'guarantor_acct_no',
]

/** How many identifier chips the sheet header shows before "+N more". */
export const SOURCE_ID_HEADER_LIMIT = 2
